import { Box, Button, Stack, Typography } from '@mui/material';
import { BoardLaneSurface } from './BoardSurfaces';
import { memo, useCallback, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { BoardColumnHeader } from './BoardColumnHeader';
import { SortableBoardCard } from './SortableBoardCard';
import { cardDragId } from './drag-ids';
import type { BoardColumnCoreFragment } from '../../../generated/graphql';

interface BoardColumnProps {
  column: BoardColumnCoreFragment;
  columns: { _id: string; name: string }[];
  onRenameColumn: (columnId: string, name: string) => void;
  /** Omit to hide the Delete column menu item (e.g., when edit mode is off). */
  onDeleteColumn?: (columnId: string) => void;
  onAddCard: (columnId: string) => void;
  onDeleteCard: (cardId: string) => void;
  onMoveCard: (cardId: string, toColumnId: string) => void;
  onOpenProject: (projectId: string) => void;
}

function BoardColumnImpl(props: BoardColumnProps) {
  const {
    column,
    columns,
    onRenameColumn,
    onDeleteColumn,
    onAddCard,
    onDeleteCard,
    onMoveCard,
    onOpenProject,
  } = props;
  const cardItems = useMemo(
    () => column.cards.map((c) => cardDragId(c._id)),
    [column.cards]
  );
  const isEmpty = column.cards.length === 0;

  // Stable refs for the BoardColumnHeader / Add CTA — let those memo'd
  // children actually skip re-render when only sibling columns change.
  const handleRename = useCallback(
    (name: string) => onRenameColumn(column._id, name),
    [onRenameColumn, column._id]
  );
  const handleDelete = useMemo(
    () => (onDeleteColumn ? () => onDeleteColumn(column._id) : undefined),
    [onDeleteColumn, column._id]
  );
  const handleAddCard = useCallback(
    () => onAddCard(column._id),
    [onAddCard, column._id]
  );

  return (
    <BoardLaneSurface
      variant="panel"
      sx={{
        minWidth: 0,
        width: '100%',
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'clamp(420px, 65dvh, 840px)' },
        scrollSnapAlign: 'start',
      }}
    >
      <BoardColumnHeader
        column={column}
        cardCount={column.cards.length}
        onRename={handleRename}
        onDelete={handleDelete}
      />
      <Box
        sx={{
          flex: 1,
          p: 1.25,
          overflowY: { xs: 'visible', md: 'auto' },
          scrollbarWidth: 'thin',
        }}
      >
        <SortableContext
          items={cardItems}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={0}>
            {column.cards.map((card) => (
              <SortableBoardCard
                key={card._id}
                card={card}
                columnId={column._id}
                columns={columns}
                onDelete={onDeleteCard}
                onMoveCard={onMoveCard}
                onOpenProject={onOpenProject}
              />
            ))}
          </Stack>
        </SortableContext>
        {isEmpty && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', mt: 2, mb: 1, px: 1 }}
          >
            <Box
              component="span"
              sx={{ display: { xs: 'none', md: 'inline' } }}
            >
              Drop a project here to mark it as {column.name.toLowerCase()}.
            </Box>
            <Box
              component="span"
              sx={{ display: { xs: 'inline', md: 'none' } }}
            >
              No projects here. Add one below or move a project here using its
              menu.
            </Box>
          </Typography>
        )}
      </Box>
      <Button
        variant="neutral"
        startIcon={<AddIcon fontSize="small" />}
        onClick={handleAddCard}
        sx={{
          m: 1,
          py: 0.625,
          px: 1.5,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          color: 'text.secondary',
          justifyContent: 'flex-start',
          gap: 0.5,
          '&:hover': {
            borderColor: 'text.secondary',
            color: 'text.primary',
            backgroundColor: 'action.hover',
          },
        }}
      >
        Add project
      </Button>
    </BoardLaneSurface>
  );
}

export const BoardColumn = memo(BoardColumnImpl);
